<script lang="ts" setup>
import { cloneDeep } from 'lodash'
import { computed, reactive, ref } from 'vue'

import { ResultId } from '@collector/sportsapi-client-legacy'
import UpdateEventParticipants from '@mobile/ActionQueue/Actions/Event/UpdateEventParticipants'
import Participant from '@mobile/components/Participant/Participant.vue'
import Tabs from '@mobile/components/Tabs/Tabs.vue'
import * as popup from '@mobile/globalState/popup'
import { byId } from '@mobile/reusables/entityUtils'
import { participantDiff } from '@mobile/reusables/eventUtils'
import PopupControls from '@mobile/views/Relation/Shared/Popup/PopupControls.vue'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import { useVerifyResultsFactory } from '@mobile/views/Relation/Shared/VerifyResults/factories/VerifyResultsFactory/useVerifyResultsFactory'

import InputNumeric from '../../../Shared/Popups/EventStats/InputNumeric.vue'
import ResultWicketsStat from './LiveViewHeader/LiveViewHeaderStats/ResultWicketsStat.vue'

// composable
const {
  probableHomeParticipant,
  probableAwayParticipant,
  probableEvent,
  actionQueue,
  sport,
} = useEventInjections()
const { getResultsIdsSet, getStatIdsSet, getDisabledResultsIdsSet } =
  useVerifyResultsFactory(probableEvent.value.sport_id)

// data
const tabs = ['Results and Team Statistics', 'Player stats']

// state
const currentTab = ref(tabs[0])
const staticHomeParticipant = reactive(cloneDeep(probableHomeParticipant.value))
const staticAwayParticipant = reactive(cloneDeep(probableAwayParticipant.value))

// computed
const results = computed(() =>
  [...getResultsIdsSet()].flatMap((resultId: ResultId) => {
    const homeParticipantStat = byId(
      staticHomeParticipant.results || [],
      resultId,
    )
    const awayParticipantStat = byId(
      staticAwayParticipant.results || [],
      resultId,
    )
    const sportResult = byId(sport.value.sportsAPIEntity.results, resultId)

    if (!homeParticipantStat || !awayParticipantStat || !sportResult) {
      return []
    }

    return [
      {
        id: resultId,
        name: sportResult.name,
        home: homeParticipantStat,
        away: awayParticipantStat,
      },
    ]
  }),
)

const statistics = computed(() =>
  [...getStatIdsSet()].flatMap((statId) => {
    const homeParticipantStat = byId(staticHomeParticipant.stats || [], statId)
    const awayParticipantStat = byId(staticAwayParticipant.stats || [], statId)
    const sportResult = byId(sport.value.sportsAPIEntity.stats.team, statId)

    if (!homeParticipantStat || !awayParticipantStat || !sportResult) {
      return []
    }

    return [
      {
        id: statId,
        name: sportResult.name,
        home: homeParticipantStat,
        away: awayParticipantStat,
      },
    ]
  }),
)

// methods
function save(): void {
  actionQueue.value.add(
    new UpdateEventParticipants({
      data: [
        participantDiff(probableHomeParticipant.value, staticHomeParticipant),
        participantDiff(probableAwayParticipant.value, staticAwayParticipant),
      ],
    }),
  )

  popup.hide()
}

function cancel(): void {
  popup.hide()
}

function shouldInputNumericBeDisabled(resultId: ResultId): boolean {
  const disabledResultsIdsSet = getDisabledResultsIdsSet?.()
  return disabledResultsIdsSet?.has(resultId) || false
}
</script>

<template>
  <Tabs
    v-model:currentTab="currentTab"
    class="p-4"
    :tabs
  >
    <div
      v-if="currentTab === tabs[0]"
      class="space-y-4"
    >
      <div class="flex items-center justify-between py-4">
        <div class="flex space-x-2">
          <Participant :participant="staticHomeParticipant" />
          <ResultWicketsStat
            class="px-2 text-xs"
            :participant="probableHomeParticipant"
          />
        </div>
        <div class="flex space-x-2">
          <ResultWicketsStat
            class="px-2 text-xs"
            :participant="probableAwayParticipant"
          />
          <Participant
            :participant="staticAwayParticipant"
            :reverse="true"
          />
        </div>
      </div>
      <div
        v-for="type in ['results', 'statistics']"
        :key="type"
      >
        <div
          v-for="item in type === 'results' ? results : statistics"
          :key="item.id"
          class="space-y-2"
        >
          <div class="flex items-center justify-between">
            <InputNumeric
              v-model:value="item.home.value"
              class="mr-4 w-14"
              :min="0"
              :disabled="
                type === 'results' &&
                shouldInputNumericBeDisabled(item.id as ResultId)
              "
            />
            <div class="text-gray text-xs">
              {{ item.name }}
            </div>
            <InputNumeric
              v-model:value="item.away.value"
              class="ml-4 w-14"
              :min="0"
              :disabled="
                type === 'results' &&
                shouldInputNumericBeDisabled(item.id as ResultId)
              "
            />
          </div>

          <hr class="border-gray border-t opacity-30" />
        </div>
      </div>
    </div>
  </Tabs>

  <PopupControls
    @confirm="save()"
    @cancel="cancel()"
  />
</template>
